import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { DEFAULT_LOCALE, type AppLocale, resolveLocale } from "@lms/backend/lib/i18n/locale";
import { useEffect, useMemo, useState } from "react";
import { IntlProvider } from "@/i18n/intl-provider";
import { ThemeProvider } from "@/components/theme-provider";

const LOCALE_STORAGE_KEY = "lms_locale";

function readStoredLocale(): AppLocale {
  return resolveLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
}

function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (isAxiosError(error) && error.response?.status === 401) return false;
          return failureCount < 2;
        },
      },
    },
  });
}

export function AppProviders({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(createAppQueryClient);
  const locale = useMemo(() => {
    try {
      return readStoredLocale();
    } catch {
      return DEFAULT_LOCALE;
    }
  }, []);
  // ACC (WCAG 3.1.1): the document's language follows the rendered locale (Hindi pages say `hi`).
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <QueryClientProvider client={queryClient}>
      <IntlProvider locale={locale}>
        <ThemeProvider>{children}</ThemeProvider>
      </IntlProvider>
    </QueryClientProvider>
  );
}
